// src/hooks/useXeroSales.ts
// ─────────────────────────────────────────────────────────────────────────────
// Wraps /api/xero/sales-analysis. Mirrors useXeroData's shape.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';

export interface MonthlyRevenuePoint { month: string; revenue: number; }

export interface ClientHistory {
  contactId: string;
  name: string;
  totalRevenue: number;
  invoiceCount: number;
  firstInvoice: string | null;
  lastInvoice: string | null;
  avgFee: number;
}

export interface TrackingCategory {
  name: string;
  options: Array<{ name: string; revenue: number }>;
}

export interface YearlyKPI {
  year: string;
  revenue: number;
  grossMargin: number;
  overheadRatio: number;
  ebitdaMargin: number;
  retentionRate: number | null;
  avgClientFee: number;
  newClients: number | null;
  churnedClients: number | null;
}

export interface XeroSales {
  monthlyRevenue: MonthlyRevenuePoint[];
  activeClients: number;
  avgFeePerClient: number;
  retentionRate: number;
  newClientsLast12m: number;
  churnedLast12m: number;
  clientDetail: ClientHistory[];
  trackingCategories: TrackingCategory[] | null;
  yearlyKPIs: YearlyKPI[];
}

export interface XeroSalesResult {
  loading: boolean;
  data: XeroSales | null;
  error: string | null;
  tenantName: string | null;
}

export function useXeroSales(tenantId: string | undefined, financialYearEnd?: string): XeroSalesResult {
  const [result, setResult] = useState<XeroSalesResult>({
    loading: true,
    data: null,
    error: null,
    tenantName: null,
  });

  useEffect(() => {
    if (!tenantId) {
      setResult({ loading: false, data: null, error: 'No Xero tenant bound to this client', tenantName: null });
      return;
    }
    const params = new URLSearchParams({ tenantId });
    if (financialYearEnd) params.set('financialYearEnd', financialYearEnd);
    const q = `?${params.toString()}`;
    Promise.all([
      fetch(`/api/xero/status?tenantId=${encodeURIComponent(tenantId)}`).then((r) => r.json()),
      fetch(`/api/xero/sales-analysis${q}`).then((r) => r.json()),
    ])
      .then(([status, sales]) => {
        if (!status.connected) {
          setResult({ loading: false, data: null, error: 'Xero not connected', tenantName: null });
          return;
        }
        if (sales.error) {
          setResult({ loading: false, data: null, error: sales.error, tenantName: status.tenantName });
          return;
        }
        setResult({ loading: false, data: sales as XeroSales, error: null, tenantName: status.tenantName });
      })
      .catch((err) => {
        setResult({ loading: false, data: null, error: err.message, tenantName: null });
      });
  }, [tenantId, financialYearEnd]);

  return result;
}
